import { LAYER_FACTORS, TRANSLATION_AMPLITUDE } from "../constants";
import { ImageConfig, ImageSize, Layer, Vector } from "../types";
import { PickedImageData } from "./ImagePickerDrawer";
import { ImageConfigUI } from "./ImageConfigUI";

interface ArchiveImageProps {
  size: ImageSize;
  el: HTMLElement;
}

interface AddToStageRequest {
  stage: HTMLElement;
  index: number;
  row: number;
  column: number;
  randomisePosition?: boolean;
  draggable?: boolean;
  debug?: boolean;
}

interface UpdateSettingRequest {
  key: string;
  value: string;
}

export class ArchiveImage extends EventTarget {
  isDragging: boolean = false;
  el: HTMLElement;
  _size: ImageSize;
  // Stored in vw units (% of viewport width) so transforms stay correct across resizes.
  offset: Vector = { x: 0, y: 0 };
  translationFactor: number = 1;
  dragStart: Vector = { x: 0, y: 0 };
  guide: HTMLElement | null = null;
  layer: Layer = "middle";
  _col: number = 0;
  _row: number = 0;
  _isPortrait: boolean = false;
  _configUI: ImageConfigUI | null = null;
  _draggable: boolean = false;
  _hidden: boolean = false;
  _editMode: boolean = window.SuburbiaConfig?.isEditMode || false;
  _previousImgState: { src: string; srcset: string; sizes: string; dataId: string; width: string; height: string } | null = null;
  // True once a swap has been written back to the post content — the new
  // attachment ID is then the canonical one and swapData is no longer saved.
  _swapSynced: boolean = false;

  constructor({ el, size }: ArchiveImageProps) {
    super();
    this.el = el;
    this._size = size;

    this.el.classList.add("archive-image", `size-${this._size}`);

    const imageWidth = parseFloat(this.img.getAttribute("width") || "0");
    const imageHeight = parseFloat(this.img.getAttribute("height") || "0");

    if (imageHeight > imageWidth) {
      this._isPortrait = true;
      this.el.classList.add("portrait");
    }
  }

  addToStage({
    stage,
    index,
    row,
    column,
    randomisePosition,
    draggable,
    debug
  }: AddToStageRequest): void {
    const grid = stage.querySelector<HTMLElement>(".slots")!;
    this.el.style.gridRowStart = `${row}`;
    this.el.style.gridRowEnd = `${row}`;
    this.el.classList.add(`col-${column}`, `row-${row}`);
    this._col = column;
    this._row = row;
    this._draggable = !!draggable;

    grid.appendChild(this.el);

    if (randomisePosition) {
      this.randomisePosition();
    }

    const displayHeight = this.getDisplayHeight();
    if (displayHeight > 0) {
      this.el.style.height = `${displayHeight}px`;
    }

    if (debug) {
      const sizeLabel = document.createElement("div");
      sizeLabel.className = "size-label absolute bottom-2 left-2 bg-black text-white text-xs px-2 py-0.5 rounded-full whitespace-nowrap opacity-50";
      sizeLabel.textContent = `${index + 1} ${this._size.toUpperCase()} - ${this.layer}`;
      this.el.appendChild(sizeLabel);
    }

    this.addMouseListeners();
  }

  // Returns the expected display height in px based on natural aspect ratio and current column width.
  // Reliable across resizes since it reads live offsetWidth rather than a cached value.
  getDisplayHeight(): number {
    const naturalWidth = parseFloat(this.img.getAttribute("width") || "0");
    const naturalHeight = parseFloat(this.img.getAttribute("height") || "0");
    const columnWidth = this.el.offsetWidth;

    // offsetWidth is 0 while the grid is unrendered (stage hidden below the md
    // breakpoint, or measured before layout). Returning 0 here would collapse the
    // row; signal "unmeasurable" so the caller can skip and retry once laid out.
    if (columnWidth <= 0) {
      return 0;
    }

    if (naturalWidth > 0 && naturalHeight > 0) {
      return columnWidth * (naturalHeight / naturalWidth);
    }

    return this.img.clientHeight || this.el.offsetHeight;
  }

  randomisePosition(): void {
    const maxShift = 0.5;
    const yMultiplier = this._row === 1 ? 1 : (Math.random() - 0.5) * 2;
    const imgHeight_vw = this.convertToVw(this.getDisplayHeight());

    if (this._row > 1) {
      const xMultiplier = this._col === 1 ? 1 : (this._col + this.colGridCount) === 13 ? -1 : (Math.random() - 0.5) * 2;
      this.offset.x += this.convertToVw(this.el.offsetWidth) * xMultiplier * maxShift;
    }

    this.offset.y += imgHeight_vw * yMultiplier * maxShift;
    this.updateTransform();
  }

  addMouseListeners() {
    this.img.addEventListener("mousedown", this.onMouseDown.bind(this));
    document.addEventListener("mouseup", this.onMouseUp.bind(this));
    document.addEventListener("mousemove", this.onMouseMove.bind(this));
  }

  addConfigUI() {
    if (!this._editMode) {
      return;
    }

    this._configUI = new ImageConfigUI();
    this._configUI.addEventListener("updateSetting", (event: Event) => {
      const detail = (event as CustomEvent).detail as UpdateSettingRequest;
      this.handleSettingChange(detail);
    });

    this.el.appendChild(this._configUI.el);

    this._configUI.updateUI({
      layer: this.layer,
      size: this._size
    });

    this.repositionConfigUI();
  }

  // The img has position:absolute inside a flex justify-center figure, so its rendered
  // top may be offset from the figure top. getBoundingClientRect on both cancels the
  // CSS transform, leaving just the local offset — which we use to anchor the toolbar.
  repositionConfigUI() {
    if (!this._configUI) return;
    const imgTop = this.img.getBoundingClientRect().top;
    const figTop = this.el.getBoundingClientRect().top;
    const toolbarH = this._configUI.el.offsetHeight || 48;
    this._configUI.el.style.top = `${imgTop - figTop - toolbarH - 4}px`;
  }

  revealUI() {
    if (!this._configUI) {
      this.addConfigUI();
    } else {
      this.repositionConfigUI();
      this._configUI.show(true);
    }
  }

  handleSettingChange({ key, value }: UpdateSettingRequest) {
    switch (key) {
      case "layer":
        this.setLayer(value as Layer);
        break;
      case "size":
        const sizeClasses = ["xs", "sm", "md", "lg", "xl"].map(s => `size-${s}`);
        this.el.classList.remove(...sizeClasses);
        this.el.classList.add(`size-${value}`);
        this._size = value as ImageSize;
        // Size changes alter the grid span, so row heights must be re-measured —
        // otherwise the edit view shows stale heights that won't match a reload.
        this.dispatchEvent(new CustomEvent("layoutChanged"));
        break;

      case "remove":
        this.setHidden(true);
        this.dispatchEvent(new CustomEvent("layoutChanged"));
        break;

      case "swap":
        this.dispatchEvent(new CustomEvent("swapRequested", { detail: { image: this } }));
        break;

      case "undoSwap":
        this.undoSwap();
        break;
    }

    this._configUI!.updateUI({
      layer: this.layer,
      size: this._size
    });
  }

  updateVisibility(): void {
    const func = this._hidden ? "add" : "remove";
    this.el.classList[func]("hidden!");
  }

  setHidden(hidden: boolean): void {
    this._hidden = hidden;
    this.updateVisibility();
  }

  setSwapSynced(synced: boolean): void {
    this._swapSynced = synced;
  }

  onMouseDown(event: MouseEvent) {
    event.preventDefault();

    if (this._draggable) {
      // Convert current vw offset to px to anchor drag origin correctly.
      const offsetX_px = (this.offset.x / 100) * window.innerWidth;
      const offsetY_px = (this.offset.y / 100) * window.innerWidth;
      this.dragStart = {
        x: event.clientX - offsetX_px,
        y: event.clientY - offsetY_px,
      };
      this.isDragging = true;
      this.el.classList.add("is-dragging");
    }

    this.el.parentElement!.appendChild(this.el);

    if (!this._configUI) {
      this.addConfigUI();
    }

    this.dispatchEvent(new CustomEvent("imageSelected", { detail: { image: this } }));
  }

  onMouseUp(_: MouseEvent) {
    this.isDragging = false;
    this.el.classList.remove("is-dragging");
  }

  onMouseMove(event: MouseEvent) {
    if (!this.isDragging) {
      return;
    }

    this.offset.x = this.convertToVw(event.clientX - this.dragStart.x);
    this.offset.y = this.convertToVw(event.clientY - this.dragStart.y);
    this.updateTransform();
  }

  setLayer(layer: Layer) {
    const oldFactor = this.translationFactor;
    this.layer = layer;
    this.translationFactor = LAYER_FACTORS[this.layer] * TRANSLATION_AMPLITUDE;

    // When changed interactively (image already placed, _row > 0), compensate offset.y
    // so the visual position is unchanged despite the new parallax rate.
    if (this._row > 0) {
      this.offset.y += this.convertToVw(window.scrollY * (oldFactor - this.translationFactor));
    }
  }

  setTransform(offset: Vector) {
    // offset arrives in vw units (as stored by getConfig), store directly.
    this.offset = { ...offset };
    this.updateTransform();
  }

  updateY(scrollY: number) {
    const parallaxY_vw = this.convertToVw(scrollY * this.translationFactor);
    this.el.style.transform = `translate(${this.offset.x}vw, ${this.offset.y + parallaxY_vw}vw)`;
  }

  private updateTransform() {
    this.el.style.transform = `translate(${this.offset.x}vw, ${-this.offset.y}vw)`;
  }

  private convertToVw(value: number): number {
    return (value / window.innerWidth) * 100;
  }

  get img(): HTMLElement {
    return this.el.querySelector("img") as HTMLImageElement;
  }

  get y(): number {
    const top = this.el.getBoundingClientRect().top + window.scrollY;
    return top + (this.offset.y / 100) * window.innerWidth;
  }

  get size(): ImageSize {
    return this._size;
  }

  get colGridCount(): number {
    switch (this._size) {
      case "xs": return 2;
      case "sm": return 3;
      case "md": return 4;
      case "lg": return 5;
      case "xl": return 6;
    }
  }

  get headerHeight(): number {
    const header = document.querySelector<HTMLElement>(".header")!;
    return header.offsetHeight;
  }

  get isPortrait(): boolean {
    return this._isPortrait;
  }

  get ui(): ImageConfigUI | null {
    return this._configUI;
  }

  swapImage(imageData: PickedImageData): void {
    const imgEl = this.img as HTMLImageElement;

    // Only capture the state of the ORIGINAL DOM image. Swapping repeatedly must
    // not overwrite it — getConfig relies on it to save an imageId that actually
    // exists in the rendered markup, otherwise restore can't find the figure.
    if (!this._previousImgState) {
      this._previousImgState = {
        src:    imgEl.src,
        srcset: imgEl.srcset,
        sizes:  imgEl.getAttribute("sizes") || "",
        dataId: imgEl.getAttribute("data-id") || "",
        width:  imgEl.getAttribute("width") || "",
        height: imgEl.getAttribute("height") || "",
      };
    }

    imgEl.src = imageData.url;
    imgEl.srcset = "";
    imgEl.removeAttribute("sizes");
    imgEl.setAttribute("data-id", String(imageData.id));
    imgEl.setAttribute("width", String(imageData.width));
    imgEl.setAttribute("height", String(imageData.height));

    const wasPortrait = this._isPortrait;
    this._isPortrait = imageData.height > imageData.width;
    if (wasPortrait !== this._isPortrait) {
      this.el.classList.toggle("portrait", this._isPortrait);
    }

    this._configUI?.showUndo(true);
  }

  undoSwap(): void {
    if (!this._previousImgState) {
      return;
    }

    const imgEl = this.img as HTMLImageElement;
    const prev = this._previousImgState;
    const swappedId = parseInt(imgEl.getAttribute("data-id") || "0", 10);

    imgEl.src = prev.src;
    imgEl.srcset = prev.srcset;
    if (prev.sizes) {
      imgEl.setAttribute("sizes", prev.sizes);
    } else {
      imgEl.removeAttribute("sizes");
    }
    imgEl.setAttribute("data-id", prev.dataId);
    imgEl.setAttribute("width", prev.width);
    imgEl.setAttribute("height", prev.height);

    const wasPortrait = this._isPortrait;
    this._isPortrait = parseFloat(prev.height) > parseFloat(prev.width);
    if (wasPortrait !== this._isPortrait) {
      this.el.classList.toggle("portrait", this._isPortrait);
    }

    // If the swap was written back to the post, the manager must revert it there too.
    if (this._swapSynced) {
      this._swapSynced = false;
      this.dispatchEvent(new CustomEvent("swapUndone", {
        detail: { image: this, fromId: swappedId, toId: parseInt(prev.dataId, 10) },
      }));
    }

    this._previousImgState = null;
    this._configUI?.showUndo(false);
    this.dispatchEvent(new CustomEvent("layoutChanged"));
  }

  getConfig(): ImageConfig {
    const imgEl = this.img as HTMLImageElement;
    // When swapped locally only, _previousImgState holds the original DOM image's
    // data-id — we must save that ID so restoreSavedLayout() can find the element.
    // Once the swap is synced to the post content, the NEW id is what the DOM will
    // render, so it becomes the canonical imageId and no swapData is needed.
    const isLocalSwap = !!this._previousImgState && !this._swapSynced;
    const domImageId = isLocalSwap
      ? parseInt(this._previousImgState!.dataId, 10)
      : parseInt(imgEl.getAttribute("data-id")!, 10);

    const config: ImageConfig = {
      layer: this.layer,
      size: this._size,
      transform: { x: this.offset.x, y: this.offset.y },
      row: this._row,
      column: this._col,
      imageId: domImageId,
      aspect: this.isPortrait ? "portrait" : "landscape",
    };

    if (this._hidden) {
      config.hidden = true;
    }

    if (isLocalSwap) {
      config.swapData = {
        src: imgEl.src,
        id: parseInt(imgEl.getAttribute("data-id")!, 10),
        width: parseFloat(imgEl.getAttribute("width") || "0"),
        height: parseFloat(imgEl.getAttribute("height") || "0"),
      };
    }

    return config;
  }
}
